/**
 * Cross-Sell B2C Service
 * Migrated from 3in1cms legacy/crossSellB2CApi.ts
 */

import { request } from "../core/request";
import { API_RESPONSE_CODES, OPERATION_IDS, PRODUCTS_APP_VERSION } from "./constants";
import type {
  IActualBackendResponse,
  ICrossSellB2CBackendRequest,
} from "../types/crossSellB2CTypes";

// Default constants
const B2C_DEFAULT_DOB = "1990-01-01";
const DEFAULT_OFFER_PRODUCT = "PL";
const DEFAULT_PROGRAM_NAME = "WEB";
const DEFAULT_SEGMENT = "CONSUMER";
const DEFAULT_SOURCING_BRANCH_ID = "WEB001";
const DEFAULT_PROCESS_SEGMENTATION_C = "WEB";
const DEFAULT_PROCESS_SEGMENTATION = "DIGITAL";
const DEFAULT_PROCESS_TYPE = "ONLINE";
const DEFAULT_LOAN_TYPE = "PL";
const DEFAULT_CURRENT_RESIDENCE_STATE = "MH";

export interface ICrossSellB2CData {
  insuranceProduct: string;
  premiumAmt: string;
  coverageAmt: string;
  policyTenure: string;
  insuranceType: string;
  externalId: string;
  CMSBenefitsExclusions: string;
  CMSFAQs: string;
  CMSHealthDeclaration: string;
  CMSTermsConditions: string;
  CMSUntickBottomDrawer: string;
  isSelected: boolean;
}

export interface ICrossSellB2CRequest {
  mobileNo: string;
  loanAmount: string;
  offerId?: string;
  dob?: string;
  processType?: string;
  processSegmentation?: string;
  icorsOfferType?: string;
}

export interface ICrossSellB2CResponse {
  productList: ICrossSellB2CData[];
}

export interface ICrossSellB2CApiResponse {
  data: ICrossSellB2CResponse;
  statusCode: number;
  status: "success" | "error";
  message: string;
}

const prepareDefaultData = (params: {
  dob: string;
  processType?: string;
  processSegmentation?: string;
}) => {
  return {
    offerProduct: DEFAULT_OFFER_PRODUCT,
    programName: DEFAULT_PROGRAM_NAME,
    segment: DEFAULT_SEGMENT,
    sourcingBranchId: DEFAULT_SOURCING_BRANCH_ID,
    processSegmentation_c: DEFAULT_PROCESS_SEGMENTATION_C,
    processSegmentation: params.processSegmentation ?? DEFAULT_PROCESS_SEGMENTATION,
    processType: params.processType ?? DEFAULT_PROCESS_TYPE,
    loanType: DEFAULT_LOAN_TYPE,
    dateOfBirth: params.dob,
    currentResidenceState: DEFAULT_CURRENT_RESIDENCE_STATE,
  };
};

/**
 * Get cross-sell B2C products
 * @param requestParams - Cross-sell request parameters
 * @returns Cross-sell B2C response with product list
 */
export const getCrossSellB2C = async (
  requestParams: ICrossSellB2CRequest
): Promise<ICrossSellB2CApiResponse> => {
  try {
    const { mobileNo, loanAmount, dob, offerId, processType, processSegmentation } = requestParams;

    const finalDob = dob || B2C_DEFAULT_DOB;
    const finalOfferId = offerId?.trim() || null;

    let defaultData = {};
    if (!finalOfferId) {
      defaultData = prepareDefaultData({
        dob: finalDob,
        processType,
        processSegmentation,
      });
    }

    const data: ICrossSellB2CBackendRequest = {
      mobileNumber: mobileNo,
      loanAmount,
      dob: finalDob,
      offerId: finalOfferId,
      appVersion: PRODUCTS_APP_VERSION,
      ...defaultData,
    };

    const response = (await request<IActualBackendResponse, ICrossSellB2CBackendRequest>({
      operationId: OPERATION_IDS.crossSellB2C,
      requestData: data,
    })) as IActualBackendResponse;

    const { statusCode, productList } = response;

    if (statusCode === API_RESPONSE_CODES.STATUS_CODE_7001) {
      const transformedProducts =
        productList?.map(
          (product) =>
            ({
              insuranceProduct: product.insuranceProduct ?? "Unknown Product",
              premiumAmt: product.premiumAmt ?? "0",
              coverageAmt: product.coverageAmt ?? "0",
              policyTenure: product.policyTenure ?? "12",
              insuranceType: product.insuranceType ?? "Insurance",
              externalId: product.externalId ?? "",
              CMSBenefitsExclusions: product.CMSBenefitsExclusions ?? "",
              CMSFAQs: product.CMSFAQs ?? "",
              CMSHealthDeclaration: product.CMSHealthDeclaration ?? "",
              CMSTermsConditions: product.CMSTermsConditions ?? "",
              CMSUntickBottomDrawer: product.CMSUntickBottomDrawer ?? "",
              isSelected: false,
            }) as ICrossSellB2CData
        ) ?? [];

      return {
        data: {
          productList: transformedProducts,
        },
        statusCode: Number(statusCode),
        status: "success",
        message: "Cross-sell products fetched successfully",
      };
    }

    if (statusCode === API_RESPONSE_CODES.STATUS_CODE_7002) {
      return {
        data: {
          productList: [],
        },
        statusCode: Number(statusCode),
        status: "success",
        message: "No cross-sell products found",
      };
    }

    return {
      data: {
        productList: [],
      },
      statusCode: Number(statusCode),
      status: "error",
      message: "Failed to fetch cross-sell products",
    };
  } catch (error) {
    console.error("Error in getCrossSellB2C", error);
    return {
      data: {
        productList: [],
      },
      statusCode: 500,
      status: "error",
      message: "Something went wrong",
    };
  }
};
